'use client';

import * as React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditNoteIcon from '@mui/icons-material/EditNote';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ScienceIcon from '@mui/icons-material/Science';
import ExtensionIcon from '@mui/icons-material/Extension';
import type { SvgIconComponent } from '@mui/icons-material';

/**
 * First-visit presentation shown on a DEMO site (rendered from the (site) layout
 * only while demo mode is on — a paid, published site never mounts it).
 *
 * It answers the four questions a prospect lands with: what am I looking at, can
 * I change it, what does it cost, and what else can it do. Slides are short on
 * purpose — the back office and the onboarding page carry the detail.
 *
 * "First visit" is per-browser (localStorage). Bump SEEN_KEY's version suffix if
 * the slides change enough that returning prospects should see them again.
 */

const SEEN_KEY = 'av:demo-intro:v1';

interface Slide {
  icon: SvgIconComponent;
  eyebrow: string;
  title: string;
  body: string;
  points?: string[];
}

const SLIDES: Slide[] = [
  {
    icon: AutoAwesomeIcon,
    eyebrow: 'Bienvenue',
    title: 'Ceci est une démonstration',
    body: 'Ce site a été préparé pour vous donner un aperçu concret du résultat. Les textes, les photos et les informations qu’il contient ne sont que des exemples temporaires : tout peut être remplacé par le vôtre.',
  },
  {
    icon: EditNoteIcon,
    eyebrow: 'Votre espace',
    title: 'Modifiez tout, vous-même',
    body: 'Le bouton en bas à droite de l’écran ouvre votre espace d’administration.',
    points: [
      'Changez n’importe quel texte et n’importe quelle image',
      'Un assistant intelligent y est intégré : décrivez la modification, il s’en charge',
    ],
  },
  {
    icon: RocketLaunchIcon,
    eyebrow: 'Mise en ligne',
    title: '20 € par mois, tout compris',
    body: 'Un abonnement unique pour rendre le site public, sans engagement.',
    points: [
      'Hébergement et maintenance inclus',
      'Support technique humain, par de vraies personnes',
      'Vous choisissez votre propre adresse internet',
    ],
  },
  {
    icon: ScienceIcon,
    eyebrow: 'Après publication',
    title: 'Deux sites plutôt qu’un',
    body: 'Vous gardez toujours deux versions : le site public, que vos clients consultent, et un site d’essai, où vous préparez vos changements. Vous les vérifiez tranquillement, puis vous publiez quand vous êtes satisfait.',
  },
  {
    icon: ExtensionIcon,
    eyebrow: 'Pour aller plus loin',
    title: 'Des fonctions à activer selon vos besoins',
    body: 'Le site peut faire bien plus qu’une vitrine :',
    points: [
      'Catalogue de produits et commande en click & collect',
      'Prise de rendez-vous en ligne',
      'Confirmations automatiques par e-mail et par SMS',
    ],
  },
];

export default function DemoIntroDialog() {
  const reduced = useReducedMotion();
  const [open, setOpen] = React.useState(false);
  const [index, setIndex] = React.useState(0);
  // Which way the next slide travels — so going back feels like going back.
  const [direction, setDirection] = React.useState(1);

  // Read the flag after mount: localStorage doesn't exist during SSR, and
  // opening on the first client render would mismatch the server HTML.
  React.useEffect(() => {
    try {
      if (!window.localStorage.getItem(SEEN_KEY)) setOpen(true);
    } catch {
      // Private mode / storage disabled — skip the intro rather than nag on
      // every page view.
    }
  }, []);

  const close = React.useCallback(() => {
    setOpen(false);
    try {
      window.localStorage.setItem(SEEN_KEY, String(Date.now()));
    } catch {
      // Nothing to do: it just means the intro may show again next visit.
    }
  }, []);

  const last = index === SLIDES.length - 1;

  const go = React.useCallback((delta: number) => {
    setDirection(delta);
    setIndex((i) => Math.min(SLIDES.length - 1, Math.max(0, i + delta)));
  }, []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight' && !last) go(1);
    if (e.key === 'ArrowLeft' && index > 0) go(-1);
  };

  const slide = SLIDES[index];
  const Icon = slide.icon;

  const body = (
    <>
      <Typography variant="overline" color="primary" sx={{ fontWeight: 700, letterSpacing: '.08em' }}>
        {slide.eyebrow}
      </Typography>
      <Typography
        id="demo-intro-title"
        variant="h5"
        component="h2"
        fontWeight={700}
        sx={{ mt: 0.5, mb: 1.5 }}
      >
        {slide.title}
      </Typography>
      <Typography color="text.secondary">{slide.body}</Typography>

      {slide.points && (
        <Stack component="ul" spacing={1} sx={{ mt: 2, pl: 0, listStyle: 'none' }}>
          {slide.points.map((point) => (
            <Stack key={point} component="li" direction="row" spacing={1.5}>
              <Box
                aria-hidden
                sx={{
                  mt: '9px',
                  flexShrink: 0,
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                }}
              />
              <Typography color="text.secondary">{point}</Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </>
  );

  return (
    <Dialog
      open={open}
      onClose={close}
      onKeyDown={onKeyDown}
      fullWidth
      maxWidth="sm"
      aria-labelledby="demo-intro-title"
      // Not part of "what the site shows" — keep it out of the view bridge's
      // serialized page, like the back-office dialog.
      data-bridge-ignore="demo-intro"
      // Never full-screen, not even on a phone: edge-to-edge, the presentation
      // reads as the site itself. Keeping a margin (so the scrimmed page shows
      // all around it) is what makes it legible as a dialog laid OVER the site.
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          m: { xs: 2, sm: 4 },
          width: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          px: { xs: 3, sm: 5 },
          pt: { xs: 4, sm: 5 },
          pb: 3,
          background: (t) =>
            `linear-gradient(135deg, ${alpha(t.palette.primary.main, 0.14)}, ${alpha(
              t.palette.secondary.main,
              0.1,
            )})`,
        }}
      >
        <IconButton
          onClick={close}
          aria-label="Fermer la présentation"
          size="small"
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: 'background.paper',
            color: 'primary.main',
            boxShadow: 1,
          }}
        >
          <Icon />
        </Box>
      </Box>

      <DialogContent sx={{ px: { xs: 3, sm: 5 }, py: 3, minHeight: { xs: 'auto', sm: 260 } }}>
        {/* Enter-only animation, keyed on the slide: the new text is in the DOM
            immediately and just slides in. No exit/AnimatePresence — waiting on
            an exit animation would leave the old slide on screen whenever the
            frame loop is throttled (background tab, headless render). */}
        {reduced ? (
          <Box>{body}</Box>
        ) : (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: direction * 32 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
          >
            {body}
          </motion.div>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 3, sm: 5 }, pb: 3, pt: 0, gap: 1 }}>
        <Stack direction="row" spacing={1} sx={{ flexGrow: 1 }} aria-hidden>
          {SLIDES.map((s, i) => (
            <Box
              key={s.title}
              sx={{
                width: i === index ? 20 : 6,
                height: 6,
                borderRadius: 3,
                transition: 'width .25s, background-color .25s',
                bgcolor: (t) =>
                  i === index ? t.palette.primary.main : alpha(t.palette.text.primary, 0.18),
              }}
            />
          ))}
        </Stack>

        {index > 0 && (
          <Button onClick={() => go(-1)} color="inherit">
            Retour
          </Button>
        )}
        {last ? (
          <Button onClick={close} variant="contained">
            Découvrir le site
          </Button>
        ) : (
          <Button onClick={() => go(1)} variant="contained">
            Suivant
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
